<script lang="ts">
  import { fly } from 'svelte/transition';
  import type {
    IToastType,
    IToastQueueItem,
    IToastQueue
  } from '../../etc/types';
  import { ToastQueue, AppState } from '../../etc/stores';
  import { Container } from '../../services/container';
  import { Auth } from '../../services/auth';

  const transitionDuration = 300;

  let isVisible = false;
  let title: string = '';
  let message: string = '';
  let type: IToastType = 'error';
  let actualId: string = '';

  const auth = Container.get(Auth);

  const sleep = async (ms: number) =>
    new Promise(r => setTimeout(() => r(0), ms));

  const sleepTransition = async () => await sleep(transitionDuration);

  let openProcess;

  const cancelOpenProcess = async () => {
    openProcess.return('canceled due new request');

    isVisible = false;

    await sleepTransition();

    ToastQueue.remove();
  };

  const closeToast = async () => {
    cancelOpenProcess();
  };

  async function* openItemGenerator(item: IToastQueueItem) {
    actualId = item.id;

    title = item.title;
    message = item.message;
    type = item.type;

    isVisible = true;

    await sleepTransition();

    yield false;

    if (actualId === item.id) {
      isVisible = false;

      await sleepTransition();

      ToastQueue.remove();

      yield false;
    }
  }

  const execQueueItem = async (queue: IToastQueue) => {
    if (!queue.length) return;

    if (!isVisible) {
      const item = queue[0];

      openProcess = openItemGenerator(item);

      await openProcess.next();

      await sleep(item.duration);

      await openProcess.next();
    } else {
      cancelOpenProcess();
    }
  };

  ToastQueue.subscribe(queue => {
    execQueueItem(queue);
  });
</script>

{#if isVisible}
  <div
    transition:fly={{ y: -32, duration: transitionDuration }}
    class={`${type} ${auth.provider}`}
  >
    <i
      class="close"
      style={`background-image: url('${process.env.BASE_URL}/images/close-icon-light.svg')`}
      on:click={closeToast}
    />
    <h4>{title}</h4>
    <p>{message}</p>
  </div>
{/if}

<style lang="scss">
  div {
    height: 76px;
    padding: 16px;
    position: absolute;
    top: 40px;
    left: 50%;
    transform: translateX(-50%);
    width: 95%;
    box-sizing: border-box;
    border-radius: 8px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex-flow: column nowrap;
    color: $color-white;
    z-index: 100;

    &.wix {
      z-index: calc(var(--above-all-z-index) - 4);
    }

    &.error {
      background-color: $color-danger;
    }

    &.success {
      background-color: $color-success;
    }

    h4 {
      font-weight: bold;
      font-size: 14px;
      line-height: 16px;
      margin-bottom: 8px;
      color: $color-white;
    }

    p {
      font-size: 12px;
      line-height: 16px;
      letter-spacing: -0.1px;
      color: $color-white;
      margin: 0;
    }
  }

  .close {
    width: 20px;
    height: 20px;
    background-size: cover;
    position: absolute;
    top: 14px;
    right: 10px;
    cursor: pointer;
  }
</style>
